import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OneOnOneColleagueItem } from '@/lib/colleagues.ts';
import {
  addFollowException,
  removeFollowException,
  type MeetingFollowExceptionItem,
} from '@/lib/meetingFollow.ts';
import { followCorrectionFor } from '@/lib/personCorrections.ts';

interface TickChange {
  colleague: OneOnOneColleagueItem;
  follow: boolean;
}

/**
 * The onboarding ticks: a tick is the standing correction that follows that
 * person, stored the moment it is made. It shows at once and stays until the
 * server has been re-read and agrees.
 */
export function useColleagueTicks(corrections: readonly MeetingFollowExceptionItem[]) {
  const queryClient = useQueryClient();
  const [inFlight, setInFlight] = useState<Record<string, boolean>>({});

  const tick = useMutation({
    mutationFn: async ({ colleague, follow }: TickChange) => {
      if (follow) {
        await addFollowException({
          target_kind: 'person',
          email: colleague.email,
          follow: true,
          record: null,
        });
        return;
      }

      const correction = followCorrectionFor(colleague, corrections);
      if (correction) await removeFollowException(correction.id);
    },
    /*
     * Settled rather than successful: dropping the optimistic tick after the
     * re-read is what reverts a pick the server refused, with no second path.
     */
    onSettled: async (_result, _error, { colleague }) => {
      await queryClient.invalidateQueries({ queryKey: ['meetingFollowExceptions'] });
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      /* Shared with the Team page, which shows whether each person is followed. */
      await queryClient.invalidateQueries({ queryKey: ['oneOnOneColleagues'] });
      setInFlight((current) =>
        Object.fromEntries(Object.entries(current).filter(([id]) => id !== colleague.id)),
      );
    },
  });

  const ticked = (colleague: OneOnOneColleagueItem) =>
    inFlight[colleague.id] ?? followCorrectionFor(colleague, corrections) !== undefined;

  const toggle = (colleague: OneOnOneColleagueItem, follow: boolean) => {
    setInFlight((current) => ({ ...current, [colleague.id]: follow }));
    tick.mutate({ colleague, follow });
  };

  return { ticked, toggle, saving: tick.isPending, failed: tick.isError };
}
